import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  Checkbox,
  IconButton,
  Pagination,
  Autocomplete,
  TextField,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { PageTitle } from "../../components-new/common";

const StyledLabel = styled(Typography)`
  color: #8089a8;
  font-size: 13px;
  font-weight: 500;
`;

const StepDesignPrinciplesModal = ({
  open,
  onCancel,
  onConfirm,
  testSteps,
  selectedStepId,
  setSelectedStepId,
  stepPage,
  setStepPage,
  stepPageCount,
  paginatedSteps,
  principlesData,
  paginatedPrinciples,
  principlePage,
  setPrinciplePage,
  principlePageCount,
  stepPrinciples,
  setStepPrinciples,
  isReadOnly,
  stepPendingImage,
  stepExistingBaseImage,
  stepPendingReferenceStepId,
  stepIsUploading,
  handleStepImageUpload,
  handleConfirmStepUpload,
  handleRemoveStepImage,
  handleSelectStepImage,
  handleSaveStepPrinciples,
  isSavingStep,
  imageSourceMode,
  setImageSourceMode,
  referenceStepId,
  setReferenceStepId,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="step-principles-dialog-title"
      PaperProps={{ sx: { borderRadius: "12px", p: 1 } }}
    >
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <PageTitle title={"Step Level Design Principles"} variant={"h6"} />
            <Typography fontSize="13px" color="#8089A8" mt={0.5}>
              Pick a step on the left, then choose the principles to apply to it.
            </Typography>
          </Box>
          <IconButton
            onClick={onConfirm}
            size="small"
            aria-label="Close step level design principles"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ borderBottom: "1px solid #e2e8f0", my: 2 }} />
        <Box display="flex" gap={2}>
          <Box flex={1} sx={{ minWidth: 0 }}>
            <StyledLabel>Select Step</StyledLabel>
            <Box
              sx={{
                maxHeight: "320px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                mt: 1,
                p: 1,
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                backgroundColor: "#fafafa",
              }}
            >
              {testSteps.length === 0 && (
                <Typography fontSize="13px" color="#8089A8" p={1}>
                  No steps available
                </Typography>
              )}
              {paginatedSteps.map((step) => {
                const isActive = selectedStepId === step.stepId;
                return (
                  <Box
                    key={step.stepId}
                    px={1}
                    py={0.5}
                    onClick={() => {
                      if (isReadOnly) return;
                      setSelectedStepId(step.stepId);
                      setPrinciplePage(1);
                    }}
                    sx={{
                      cursor: isReadOnly ? "default" : "pointer",
                      borderRadius: 1,
                      backgroundColor: isActive ? "#fff0ed" : "transparent",
                      border: isActive ? "1px solid #ff5336" : "1px solid transparent",
                      "&:hover": { backgroundColor: isReadOnly ? "" : "#f1f5f9" },
                    }}
                  >
                    <Typography fontSize="13px" color="#334155">
                      Step {step.stepNumber} — {step.keyword} ({step.action})
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            {stepPageCount > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                <Pagination
                  count={stepPageCount}
                  page={stepPage}
                  onChange={(e, value) => setStepPage(value)}
                  size="small"
                  siblingCount={0}
                  boundaryCount={1}
                />
              </Box>
            )}
          </Box>

          <Box flex={1} sx={{ minWidth: 0 }}>
            {selectedStepId ? (
              <>
                <StyledLabel>Design Principles for this Step</StyledLabel>
                <Box
                  sx={{
                    maxHeight: "320px",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    mt: 1,
                    p: 1,
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    backgroundColor: "#fafafa",
                  }}
                >
                  {!principlesData?.principles?.length && (
                    <Typography fontSize="13px" color="#8089A8" p={1}>
                      No principles available
                    </Typography>
                  )}
                  {paginatedPrinciples.map((p) => {
                    const selectedForStep = stepPrinciples[selectedStepId] || [];
                    const isSelected = selectedForStep.includes(p.id);
                    return (
                      <Box
                        key={p.id}
                        display="flex"
                        alignItems="center"
                        px={1}
                        py={0.5}
                        sx={{
                          cursor: isReadOnly ? "default" : "pointer",
                          borderRadius: 1,
                          backgroundColor: isSelected ? "#fff0ed" : "transparent",
                          "&:hover": {
                            backgroundColor: isReadOnly ? "" : isSelected ? "#ffe6e0" : "#f1f5f9",
                          },
                        }}
                        onClick={() => {
                          if (isReadOnly) return;
                          setStepPrinciples((prev) => {
                            const current = prev[selectedStepId] || [];
                            const next = current.includes(p.id)
                              ? current.filter((id) => id !== p.id)
                              : [...current, p.id];
                            return { ...prev, [selectedStepId]: next };
                          });
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          disabled={isReadOnly}
                          size="small"
                          sx={{ p: 0.5, mr: 1, "&.Mui-checked": { color: "#ff5336" } }}
                          inputProps={{ "aria-label": `Toggle ${p.principleName} for selected step` }}
                        />
                        <Typography fontSize="13px" color="#334155">
                          {p.principleName}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                {principlePageCount > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                    <Pagination
                      count={principlePageCount}
                      page={principlePage}
                      onChange={(e, value) => setPrinciplePage(value)}
                      size="small"
                      siblingCount={0}
                      boundaryCount={1}
                    />
                  </Box>
                )}
                <Box mt={2}>
                  <StyledLabel>Image Source</StyledLabel>
                  {(!imageSourceMode || imageSourceMode === "choosing") &&
                    (isReadOnly ? (
                      <Typography fontSize="12px" color="#8089A8" mt={1}>
                        No image source selected
                      </Typography>
                    ) : (
                      <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setImageSourceMode("image")}
                          sx={{
                            textTransform: "none",
                            borderColor: "#ff5336",
                            color: "#ff5336",
                            "&:hover": { borderColor: "#e04a2f", backgroundColor: "#fff5f3" },
                          }}
                        >
                          Upload Image
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setImageSourceMode("step")}
                          sx={{
                            textTransform: "none",
                            borderColor: "#ff5336",
                            color: "#ff5336",
                            "&:hover": { borderColor: "#e04a2f", backgroundColor: "#fff5f3" },
                          }}
                        >
                          Select Test Step
                        </Button>
                      </Box>
                    ))}

                  {/* Source = uploaded image (existing UI, unchanged) */}
                  {imageSourceMode === "image" && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        border: "1px dashed #e2e8f0",
                        borderRadius: "8px",
                        backgroundColor: "#fafafa",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                        "&:hover": { borderColor: isReadOnly ? "#e2e8f0" : "#ff5336" },
                        transition: "border-color 0.2s",
                      }}
                    >
                      {stepPendingImage ? (
                        <Typography fontSize="12px" color="#334155" flex={1}>
                          {stepPendingImage.name}
                        </Typography>
                      ) : stepExistingBaseImage ? (
                        <Typography fontSize="12px" color="#334155" flex={1}>
                          {stepExistingBaseImage.name}
                        </Typography>
                      ) : stepPendingReferenceStepId ? (
                        <Typography fontSize="12px" color="#8089A8" flex={1}>
                          {(() => {
                            const refStep = testSteps.find(
                              (s) => s.stepId === stepPendingReferenceStepId,
                            );
                            return refStep
                              ? `Step ${refStep.stepNumber} — ${refStep.keyword} (${refStep.action}) selected — pending screenshot`
                              : `Step ${stepPendingReferenceStepId} selected — pending screenshot`;
                          })()}
                        </Typography>
                      ) : (
                        <Typography fontSize="12px" color="#8089A8" flex={1}>
                          No image selected
                        </Typography>
                      )}

                      {!isReadOnly && !stepPendingReferenceStepId && (
                        <>
                          <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            aria-label={
                              stepPendingImage || stepExistingBaseImage
                                ? "Change step design principle image"
                                : "Select step design principle image"
                            }
                            sx={{
                              textTransform: "none",
                              borderColor: "#ff5336",
                              color: "#ff5336",
                              "&:hover": { borderColor: "#e04a2f", backgroundColor: "#fff5f3" },
                            }}
                          >
                            {stepPendingImage || stepExistingBaseImage
                              ? "Change Image"
                              : "Select Image"}
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={handleStepImageUpload}
                              aria-label="Upload step design principle image"
                            />
                          </Button>

                          {stepPendingImage && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={handleConfirmStepUpload}
                              disabled={stepIsUploading}
                              aria-label="Upload step design principle image to server"
                              sx={{
                                textTransform: "none",
                                backgroundColor: "#ff5336",
                                color: "#fff",
                                "&:hover": { backgroundColor: "#e04a2f" },
                              }}
                            >
                              {stepIsUploading ? "Uploading..." : "Upload"}
                            </Button>
                          )}

                          {(stepPendingImage || stepExistingBaseImage) && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={handleRemoveStepImage}
                              aria-label="Delete step design principle image"
                              sx={{ background: "#fee2e2" }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </>
                      )}

                      {!isReadOnly && stepPendingReferenceStepId && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            handleRemoveStepImage();
                            setReferenceStepId(null);
                            setImageSourceMode("choosing");
                          }}
                          aria-label="Remove pending step reference"
                          sx={{ background: "#fee2e2" }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  )}

                  {/* Source = test step (searchable; excludes the configured step) */}
                  {imageSourceMode === "step" && (
                    <Box sx={{ mt: 1 }}>
                      {(() => {
                        const options = testSteps.filter((s) => s.stepId !== selectedStepId);
                        if (options.length === 0) {
                          return (
                            <Typography fontSize="13px" color="#8089A8" p={1}>
                              No other steps available
                            </Typography>
                          );
                        }
                        return (
                          <Autocomplete
                            size="small"
                            disabled={isReadOnly}
                            options={options}
                            value={options.find((s) => s.stepId === referenceStepId) || null}
                            onChange={(e, newValue) => {
                              const id = newValue ? newValue.stepId : null;
                              setReferenceStepId(id);
                              if (id) handleSelectStepImage(id);
                            }}
                            isOptionEqualToValue={(opt, val) => opt.stepId === val.stepId}
                            getOptionLabel={(step) =>
                              `Step ${step.stepNumber} — ${step.keyword} (${step.action})`
                            }
                            renderOption={(props, step) => (
                              <li {...props} key={step.stepId} style={{ fontSize: "13px" }}>
                                Step {step.stepNumber} — {step.keyword} ({step.action})
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Search a step"
                                aria-label="Search a step to borrow its image from"
                              />
                            )}
                            sx={{
                              backgroundColor: "#fafafa",
                              "& .MuiOutlinedInput-root": {
                                fontSize: "13px",
                                "& fieldset": { borderColor: "#e2e8f0" },
                                "&:hover fieldset": { borderColor: "#ff5336" },
                                "&.Mui-focused fieldset": { borderColor: "#ff5336" },
                              },
                            }}
                          />
                        );
                      })()}
                    </Box>
                  )}

                  {/* Switch source after a mode is picked */}
                  {(imageSourceMode === "image" || imageSourceMode === "step") && !isReadOnly && (
                    <Button
                      size="small"
                      onClick={() => setImageSourceMode("choosing")}
                      sx={{ mt: 1, textTransform: "none", color: "#8089A8" }}
                    >
                      Change source
                    </Button>
                  )}
                </Box>
                <Button
                  variant="contained"
                  onClick={handleSaveStepPrinciples}
                  disabled={isReadOnly || isSavingStep}
                  sx={{
                    mt: 2,
                    textTransform: "none",
                    backgroundColor: "#ff5336",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#e04a2f" },
                  }}
                >
                  {isSavingStep ? "Saving..." : "Save"}
                </Button>
              </>
            ) : (
              <Box
                sx={{
                  minHeight: "320px",
                  mt: "29px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  backgroundColor: "#fafafa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                }}
              >
                <Typography fontSize="13px" color="#8089A8" textAlign="center">
                  Select a step to view its design principles
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} sx={{ color: "#ff5336", textTransform: "none" }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StepDesignPrinciplesModal;
